import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import useProviderService from '../../hooks/useProviderService';
import useDocumentService from '../../hooks/useDocumentService';
import useInvoiceService from '../../hooks/useInvoiceService';
import useOrderService from '../../hooks/useOrderService';
import { useSnackbar } from '../../components/SnackbarProvider';
import { Add as AddIcon } from '@mui/icons-material';

import {
    TextField,
    Button,
    Typography,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogContent,
    DialogActions,
    DialogTitle,
    CircularProgress,
    Paper,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import Accordion from '../../components/Accordion';
import InvoiceRetentionTable from '../../components/InvoiceRetentionTable';
import CreatePdf from '../../components/CreatePdf';
import { formatDate, handleApiError, itemsPerPage, IconBtn } from '../../components/Utils';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';

function EditProvider() {
    const { id } = useParams();
    const navigate = useNavigate();
    const showSnackbar = useSnackbar();

    const { getProvider, updateProvider, disableProvider } = useProviderService();
    const { getDocuments, addDocument, getDocumentBlob, disableDocument } = useDocumentService();
    const { getInvoices, disableInvoice } = useInvoiceService();
    const { getOrders, disableOrder } = useOrderService();

    // --- General loading (provider form only) ---
    const [loading, setLoading] = useState({ general: true, update: false });
    const [error, setError] = useState(null);

    const [providerForm, setProviderForm] = useState({});

    // --- Documents section state ---
    const [documents, setDocuments] = useState([]);
    const [documentsPage, setDocumentsPage] = useState(1);
    const [documentsTotal, setDocumentsTotal] = useState(0);
    const [loadingDocuments, setLoadingDocuments] = useState(false);
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploadError, setUploadError] = useState(null);

    // --- Orders section state ---
    const [orders, setOrders] = useState([]);
    const [ordersPage, setOrdersPage] = useState(1);
    const [ordersTotal, setOrdersTotal] = useState(0);
    const [loadingOrders, setLoadingOrders] = useState(false);

    // --- Invoices section state ---
    const [invoices, setInvoices] = useState([]);
    const [invoicesPage, setInvoicesPage] = useState(1);
    const [invoicesTotal, setInvoicesTotal] = useState(0);
    const [loadingInvoices, setLoadingInvoices] = useState(false);
    const [totalPriceSum, setTotalPriceSum] = useState(null);
    const [paidAmountSum, setPaidAmountSum] = useState(null);
    const [remainingAmountSum, setRemainingAmountSum] = useState(null);
    const [totalRetention, setTotalRetention] = useState(null);
    const [paidRetention, setPaidRetention] = useState(null);
    const [remainingRetentionSum, setRemainingRetentionSum] = useState(null);

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [deleteId, setDeleteId] = useState(false);
    const [typeDelete, setTypeDelete] = useState(false);

    // -------------------------------------------------------------------------
    // Fetches
    // -------------------------------------------------------------------------

    const fetchProvider = useCallback(async () => {
        try {
            setLoading(prev => ({ ...prev, general: true }));
            const result = await getProvider(id);
            const provider = result.data.provider;
            setProviderForm(provider);
            setProviderForm(prevState => ({ ...prevState, provider_id: id }));
        } catch (error) {
            setError(handleApiError(error, 'Error obteniendo proveedor.'));
        } finally {
            setLoading(prev => ({ ...prev, general: false }));
        }
    }, [id]);

    const fetchDocuments = useCallback(async () => {
        try {
            setLoadingDocuments(true);
            const result = await getDocuments({
                provider_id: id,
                limit: itemsPerPage,
                offset: (documentsPage - 1) * itemsPerPage,
            });
            setDocuments(result.data.items);
            setDocumentsTotal(result.data.total);
        } catch (error) {
            showSnackbar(handleApiError(error, 'Error obteniendo documentos.'), 'error');
        } finally {
            setLoadingDocuments(false);
        }
    }, [id, documentsPage]);

    const fetchOrders = useCallback(async () => {
        try {
            setLoadingOrders(true);
            const result = await getOrders({
                provider_id: id,
                limit: itemsPerPage,
                offset: (ordersPage - 1) * itemsPerPage,
                disabled: false,
            });
            setOrders(result.data.items);
            setOrdersTotal(result.data.total);
        } catch (error) {
            showSnackbar(handleApiError(error, 'Error obteniendo pedidos.'), 'error');
        } finally {
            setLoadingOrders(false);
        }
    }, [id, ordersPage]);

    const fetchInvoices = useCallback(async () => {
        try {
            setLoadingInvoices(true);
            const result = await getInvoices({
                provider_id: id,
                only_provider_invoices: true,
                limit: itemsPerPage,
                offset: (invoicesPage - 1) * itemsPerPage,
                disabled: false,
            });
            setInvoices(result.data.items);
            setInvoicesTotal(result.data.total);
            setTotalPriceSum(result.data.total_price_sum);
            setPaidAmountSum(result.data.paid_amount_sum);
            setRemainingAmountSum(result.data.remaining_amount_sum);
            setTotalRetention(result.data.total_retentions);
            setPaidRetention(result.data.paid_retention);
            setRemainingRetentionSum(result.data.remaining_retention_sum);
        } catch (error) {
            showSnackbar(handleApiError(error, 'Error obteniendo facturas.'), 'error');
        } finally {
            setLoadingInvoices(false);
        }
    }, [id, invoicesPage]);

    // -------------------------------------------------------------------------
    // Effects – one per section
    // -------------------------------------------------------------------------

    useEffect(() => {
        fetchProvider();
    }, []);

    useEffect(() => {
        fetchDocuments();
    }, [documentsPage]);

    useEffect(() => {
        fetchOrders();
    }, [ordersPage]);

    useEffect(() => {
        fetchInvoices();
    }, [invoicesPage]);

    // -------------------------------------------------------------------------
    // Handlers
    // -------------------------------------------------------------------------

    const handleChange = (e) => {
        const { name, value } = e.target;
        setProviderForm({ ...providerForm, [name]: value });
    };

    const validateForm = () => {
        if (!providerForm.name) {
            return "Por favor, añada un nombre";
        }
        if (providerForm.email && providerForm.email.length > 36) {
            return "El email no debe exceder los 36 caracteres";
        }
        if (providerForm.name.length > 55) {
            return "El nombre no debe exceder los 55 caracteres";
        }
        if (providerForm.country && providerForm.country.length > 36) {
            return "El pais no debe exceder los 36 caracteres";
        }
        if (providerForm.cp && providerForm.cp.length > 9) {
            return "El codigo postal no debe exceder los 9 caracteres";
        }
        if (providerForm.town && providerForm.town.length > 150) {
            return "La comunidad autonoma no debe exceder los 150 caracteres";
        }
        if (providerForm.province && providerForm.province.length > 150) {
            return "La provincia no debe exceder los 150 caracteres";
        }
        if (providerForm.surname && providerForm.surname.length > 36) {
            return "Los apellidos no deben exceder los 36 caracteres";
        }
        if ((providerForm.doc_type && !providerForm.documentation) || (!providerForm.doc_type && providerForm.documentation)) {
            return "Si quiere poner una documentación, debe rellenar ambos campos (tipo y documentación).";
        }
        if (providerForm.documentation && providerForm.documentation.length > 15) {
            return "La documentación no debe exceder los 15 caracteres.";
        }
        if (providerForm.address && providerForm.address.length > 150) {
            return "La dirección no debe exceder los 150 caracteres";
        }
        if (providerForm.telephone_number && providerForm.telephone_number.length > 15) {
            return "El teléfono no debe exceder los 15 caracteres";
        }
        if (providerForm.secondary_telephone_number && providerForm.secondary_telephone_number.length > 15) {
            return "El teléfono secundario no debe exceder los 15 caracteres";
        }
        if (providerForm.commercial_name && providerForm.commercial_name.length > 36) {
            return "El alias no debe exceder los 36 caracteres";
        }
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationError = validateForm();
        if (validationError) {
            setError(validationError);
            return;
        }
        try {
            setError(null);
            setLoading(prev => ({ ...prev, update: true }));
            await updateProvider(providerForm);
            showSnackbar('Proveedor actualizado correctamente', 'success');
            fetchProvider();
        } catch (error) {
            const msg = handleApiError(error, 'Error al actualizar el proveedor.');
            setError(msg);
            showSnackbar(msg, 'error');
        } finally {
            setLoading(prev => ({ ...prev, update: false }));
        }
    };

    const handleFileChange = (e) => {
        setSelectedFile(e.target.files[0]);
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            setUploadError("Por favor selecciona un archivo");
            return;
        }
        const formData = new FormData();
        formData.append('document', selectedFile);
        formData.append('provider_id', id);

        try {
            setLoadingDocuments(true);
            await addDocument(formData);
            showSnackbar('Documento subido correctamente', 'success');
            fetchDocuments();
            setUploadModalOpen(false);
            setSelectedFile(null);
            setUploadError(null);
        } catch (error) {
            const msg = handleApiError(error, 'Error al subir el documento.');
            setUploadError(msg);
            showSnackbar(msg, 'error');
        } finally {
            setLoadingDocuments(false);
        }
    };

    const handleDownloadDocument = async (documentId, documentName) => {
        try {
            const response = await getDocumentBlob(documentId);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', documentName);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            setError(handleApiError(error, 'Error al descargar el documento.'));
        }
    };

    const cellStyle = {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: '75px'
    };

    const handleOpenDeleteDialog = (itemId, type) => {
        setDeleteId(itemId);
        setTypeDelete(type);
        setOpenDeleteDialog(true);
    };

    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
    };

    const handleDeleteItem = async () => {
        try {
            if (typeDelete === '/orders/disable') {
                await disableOrder(deleteId);
                fetchOrders();
            } else if (typeDelete === '/invoices/disable') {
                await disableInvoice(deleteId);
                fetchInvoices();
            } else if (typeDelete === '/providers/disable') {
                await disableProvider(deleteId);
            } else if (typeDelete === '/documents/disable') {
                await disableDocument(deleteId);
                fetchDocuments();
            }

            showSnackbar('Eliminado correctamente', 'success');
            if (typeDelete === '/providers/disable') {
                navigate(`/providers/list`);
            }
            setOpenDeleteDialog(false);
        } catch (error) {
            const msg = handleApiError(error, 'Error al eliminar el elemento.');
            showSnackbar(msg, 'error');
        }
    };

    // -------------------------------------------------------------------------
    // Render
    // -------------------------------------------------------------------------

    return (
        <div style={{ padding: 20 }}>
            {loading?.general && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                    <CircularProgress />
                </div>
            )}
            {!loading?.general && (
                <div>
                    <Grid container spacing={3} style={{ position: 'fixed', top: 60, right: 10, zIndex: 1100, width: 'auto' }}>
                        <Grid item>
                            <Paper elevation={0} style={{ padding: '10px', textAlign: 'center', backgroundColor: '#fff' }}>
                                <Box mb={2}>
                                    <Typography variant="h5" component="h1" gutterBottom>
                                        {providerForm.code} - {providerForm.name}
                                    </Typography>
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} md={10} lg={8}>
                        {/* Provider form accordion */}
                        <Accordion title={`${providerForm.code} ${providerForm.name}`}>
                            <form onSubmit={handleSubmit} style={{ padding: '20px' }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} style={{ marginTop: "20px" }}>
                                        <Typography variant="h6">Información Principal</Typography>
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <TextField
                                            fullWidth
                                            label="Nombre"
                                            name="name"
                                            value={providerForm.name}
                                            onChange={handleChange}
                                            margin="normal"
                                            InputLabelProps={{ shrink: !!providerForm.name }}
                                            inputProps={{ maxLength: 55 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <TextField
                                            fullWidth
                                            label="Email"
                                            name="email"
                                            value={providerForm.email}
                                            onChange={handleChange}
                                            margin="normal"
                                            InputLabelProps={{ shrink: !!providerForm.email }}
                                            inputProps={{ maxLength: 36 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <TextField
                                            fullWidth
                                            label="Teléfono"
                                            name="telephone_number"
                                            value={providerForm.telephone_number}
                                            onChange={handleChange}
                                            margin="normal"
                                            InputLabelProps={{ shrink: !!providerForm.telephone_number }}
                                            inputProps={{ maxLength: 15 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <TextField
                                            fullWidth
                                            label="Teléfono secundario"
                                            name="secondary_telephone_number"
                                            value={providerForm.secondary_telephone_number}
                                            onChange={handleChange}
                                            margin="normal"
                                            InputLabelProps={{ shrink: !!providerForm.secondary_telephone_number }}
                                            inputProps={{ maxLength: 15 }}
                                        />
                                    </Grid>
                                    <Grid item xs={10} md={2}>
                                        <TextField
                                            fullWidth
                                            label="Nombre comercial (alias)"
                                            name="commercial_name"
                                            value={providerForm.commercial_name}
                                            onChange={handleChange}
                                            margin="normal"
                                            InputLabelProps={{ shrink: !!providerForm.commercial_name }}
                                            inputProps={{ maxLength: 36 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} style={{ marginTop: "20px" }}>
                                        <Typography variant="h6">Documentación</Typography>
                                    </Grid>
                                    <Grid item xs={10} md={2}>
                                        <FormControl style={{ width: "100%" }}>
                                            <InputLabel id="document-type-label">
                                                Documentación
                                            </InputLabel>
                                            <Select
                                                labelId="document-type-label"
                                                id="document-type"
                                                label='Tipo documentación'
                                                value={providerForm.doc_type}
                                                onChange={handleChange}
                                                name="doc_type"
                                                inputProps={{ id: 'document-type' }}
                                            >
                                                <MenuItem value="DNI">DNI</MenuItem>
                                                <MenuItem value="NIF">NIF</MenuItem>
                                                <MenuItem value="NIE">NIE</MenuItem>
                                                <MenuItem value="CIF">CIF</MenuItem>
                                                <MenuItem value="DOC_EXTRANJERA">DOC EXTRANJERA</MenuItem>
                                                <MenuItem value="OTRO">OTRO</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={10} md={2}>
                                        <TextField
                                            fullWidth
                                            label="Documentación"
                                            name="documentation"
                                            value={providerForm.documentation}
                                            onChange={handleChange}
                                            inputProps={{ maxLength: 15 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} style={{ marginTop: "20px" }}>
                                        <Typography variant="h6">Dirección</Typography>
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <TextField
                                            fullWidth
                                            label="País"
                                            name="country"
                                            value={providerForm.country}
                                            onChange={handleChange}
                                            margin="normal"
                                            InputLabelProps={{ shrink: !!providerForm.country }}
                                            inputProps={{ maxLength: 36 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <TextField
                                            fullWidth
                                            label="Población"
                                            name="town"
                                            value={providerForm.town}
                                            onChange={handleChange}
                                            margin="normal"
                                            InputLabelProps={{ shrink: !!providerForm.town }}
                                            inputProps={{ maxLength: 150 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <TextField
                                            fullWidth
                                            label="Provincia"
                                            name="province"
                                            value={providerForm.province}
                                            onChange={handleChange}
                                            margin="normal"
                                            InputLabelProps={{ shrink: !!providerForm.province }}
                                            inputProps={{ maxLength: 150 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <TextField
                                            fullWidth
                                            label="Código postal"
                                            name="cp"
                                            value={providerForm.cp}
                                            onChange={handleChange}
                                            margin="normal"
                                            InputLabelProps={{ shrink: !!providerForm.cp }}
                                            inputProps={{ maxLength: 9 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Dirección"
                                            name="address"
                                            value={providerForm.address}
                                            onChange={handleChange}
                                            margin="normal"
                                            InputLabelProps={{ shrink: !!providerForm.address }}
                                            inputProps={{ maxLength: 150 }}
                                        />
                                    </Grid>
                                </Grid>
                                <br />
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            onClick={() => handleOpenDeleteDialog(id, '/providers/disable')}
                                            fullWidth
                                            style={{ backgroundColor: 'red' }}>
                                            Eliminar
                                        </Button>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            color="primary"
                                            disabled={loading?.update}
                                            fullWidth>
                                            {loading?.update ? (
                                                <CircularProgress size={24} color="inherit" />
                                            ) : (
                                                'Actualizar'
                                            )}
                                        </Button>
                                    </Grid>
                                </Grid>
                                {error && (
                                    <Typography variant="body2" color="error" style={{ marginTop: 10 }}>
                                        {error}
                                    </Typography>
                                )}
                            </form>
                        </Accordion>

                        {/* Documents accordion – paginated */}
                        <Accordion
                            title="Documentos"
                            paginate
                            totalItems={documentsTotal}
                            currentPage={documentsPage}
                            onPageChange={setDocumentsPage}
                        >
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => setUploadModalOpen(true)}
                                startIcon={<AddIcon />}
                                style={{ marginTop: 10 }}
                            >
                                Subir documento
                            </Button>
                            {loadingDocuments ? (
                                <CircularProgress />
                            ) : (
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Nombre</TableCell>
                                                <TableCell>Acciones</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {documents.filter(item => !item.disabled).map(item => (
                                                <TableRow key={item.id}>
                                                    <TableCell>
                                                        <IconBtn
                                                            icon={<DownloadIcon />}
                                                            title="Descargar"
                                                            onClick={() => handleDownloadDocument(item.id, item.name)}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <IconBtn
                                                            icon={<DeleteIcon />}
                                                            title="Eliminar"
                                                            color="error"
                                                            onClick={() => handleOpenDeleteDialog(item.id, '/documents/disable')}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                            <Dialog open={uploadModalOpen} onClose={() => setUploadModalOpen(false)}>
                                <DialogTitle>Subir documento (pdf, word, excel o imagen hasta 20mb)</DialogTitle>
                                <DialogContent>
                                    <input type="file" onChange={handleFileChange} />
                                    {uploadError && (
                                        <Typography variant="body2" color="error" style={{ marginTop: 10 }}>
                                            {uploadError}
                                        </Typography>
                                    )}
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={() => setUploadModalOpen(false)} color="primary">
                                        Cancelar
                                    </Button>
                                    <Button onClick={handleUpload} color="primary" variant='contained'>
                                        Enviar
                                    </Button>
                                </DialogActions>
                            </Dialog>
                        </Accordion>

                        {/* Orders accordion – paginated */}
                        <Accordion
                            title='Pedidos'
                            paginate
                            totalItems={ordersTotal}
                            currentPage={ordersPage}
                            onPageChange={setOrdersPage}
                        >
                            {loadingOrders ? (
                                <CircularProgress />
                            ) : (
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>
                                                    <Typography>Código</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography>Obra</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography>Estado</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography>Fecha</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography>Precio total</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography>Acciones</Typography>
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {orders.filter(item => !item.disabled).map(item => (
                                                <TableRow key={item.id}>
                                                    <TableCell component="th" scope="row" sx={cellStyle}>
                                                        <Link to={`/orders/update/${item.id}`}>
                                                            {item.code || '-'}
                                                        </Link>
                                                    </TableCell>
                                                    <TableCell component="th" scope="row" sx={cellStyle}>
                                                        {item.building ? (
                                                            <Link to={`/buildings/update/${item.building.id}`}>
                                                                {item.building.code || '-'}
                                                            </Link>
                                                        ) : (
                                                            '-'
                                                        )}
                                                    </TableCell>
                                                    <TableCell component="th" scope="row" sx={cellStyle}>
                                                        {item.order_status === 'partle_accepted' ? 'PARCIALMENTE ACEPTADO' :
                                                            item.order_status === 'received' ? 'RECIBIDO' :
                                                                item.order_status === 'pending' ? 'PENDIENTE' :
                                                                    item.order_status || '-'}
                                                    </TableCell>
                                                    <TableCell sx={cellStyle}>
                                                        {formatDate(item.order_date) || '-'}
                                                    </TableCell>
                                                    <TableCell sx={cellStyle}>
                                                        {item.total_price ? item.total_price.toLocaleString('es-ES', { useGrouping: true, minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '€' : '0,00€'}
                                                    </TableCell>
                                                    <TableCell>
                                                        <CreatePdf endpoint={`/orders/${item.id}/generate-pdf`}></CreatePdf>
                                                        <IconBtn
                                                            icon={<DeleteIcon />}
                                                            title="Eliminar"
                                                            color="error"
                                                            onClick={() => handleOpenDeleteDialog(item.id, '/orders/disable')}
                                                            sx={{ ml: 2 }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Accordion>

                        {/* Invoices – paginated */}
                        <InvoiceRetentionTable
                            isInvoice={true}
                            isClient={false}
                            showBuilding={true}
                            uploadDocument={true}
                            showClientOrProvider={false}
                            title='Facturas'
                            totalAmount={totalPriceSum}
                            paidAmount={paidAmountSum}
                            remainingAmountSum={remainingAmountSum}
                            invoices={invoices}
                            fetch={fetchInvoices}
                            handleOpenDeleteDialog={handleOpenDeleteDialog}
                            paginate={true}
                            totalItems={invoicesTotal}
                            currentPage={invoicesPage}
                            onPageChange={setInvoicesPage}
                            exportParams={{ provider_id: id, only_provider_invoices: true }}
                        />
                        <InvoiceRetentionTable
                            isInvoice={false}
                            isClient={false}
                            showBuilding={true}
                            showClientOrProvider={false}
                            title='Retenciones'
                            totalAmount={totalRetention}
                            paidAmount={paidRetention}
                            remainingAmountSum={remainingRetentionSum}
                            invoices={invoices}
                            fetch={fetchInvoices}
                            handleOpenDeleteDialog={handleOpenDeleteDialog}
                            paginate={true}
                            totalItems={invoicesTotal}
                            currentPage={invoicesPage}
                            onPageChange={setInvoicesPage}
                        />

                        {/* Confirm delete dialog */}
                        <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
                            <DialogTitle>¿Estás seguro?</DialogTitle>
                            <DialogActions>
                                <Button onClick={handleCloseDeleteDialog} color="primary">
                                    Volver
                                </Button>
                                <Button onClick={handleDeleteItem} color="error">
                                    Eliminar
                                </Button>
                            </DialogActions>
                        </Dialog>
                    </Grid>
                </div>
            )}
        </div>
    );
}

export default EditProvider;
